import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { processTemplateService } from '../../../services/processTemplateService'
import { GroupHeader, GroupItems, textFieldStyle } from './CreateProcessTemplateDialog'
import { toast } from 'react-toastify'

const EmailFieldsSettingUI = ({ processTemplate, setProcessTemplate }) => {
  const { user } = useAuthContext()
  const [fields, setFields] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedFields, setSelectedFields] = useState([])

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      if (processTemplate?.id) {
        try {
          // Fetch non-protected, non-excluded eligible fields
          const eligibleFields = await processTemplateService.getEmailFields(
            user,
            processTemplate.id
          )
          setFields(eligibleFields)

          // Load current JSONB config
          const configs = processTemplate?.email_notification_field_configs || {}
          const currentIds = configs.PROCESS_CLOSURE || configs['process-closed'] || configs.PROCESS_CLOSED || []
          
          const initialSelected = eligibleFields.filter(f => currentIds.includes(f.id))
          setSelectedFields(initialSelected)
        } catch (error) {
          console.error('Error loading email fields config:', error)
          toast.error('Failed to load email fields config.')
        }
      }
      setLoading(false)
    })()
  }, [processTemplate?.id])

  const handleFieldsChange = (event, value) => {
    setSelectedFields(value)
    
    // Update processTemplate state in the parent
    const currentConfigs = processTemplate?.email_notification_field_configs || {}
    const fieldIds = value.map(f => f.id)
    
    setProcessTemplate({
      ...processTemplate,
      email_notification_field_configs: {
        ...currentConfigs,
        PROCESS_CLOSURE: fieldIds
      }
    })
  }

  if (loading) {
    return (
      <div
        style={{
          height: '40vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress color="primary" size={40} />
      </div>
    )
  }

  return (
    <div>
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
        Process Closure Email Fields
      </Typography>
      <Typography fontSize={'11px'} variant="caption" color={'GrayText'} display="block" sx={{ mb: 2 }}>
        Select the data entry fields that should be automatically included in the process closure email template under the [[field_value_content]] placeholder. Fields are automatically sorted by activity rank, page, and sequence.
      </Typography>
      <FormControl
        variant="outlined"
        fullWidth
        size="small"
        color="primary"
      >
        <Autocomplete
          multiple
          id="select-closure-email-fields"
          options={fields}
          disableCloseOnSelect
          groupBy={(option) => {
            const act = option?.activity_name || option?.activity_template_name || 'General'
            const pg = option?.page_name || 'General'
            return `${act} / ${pg}`
          }}
          size="small"
          getOptionLabel={(item) => item?.field_template_name}
          value={selectedFields}
          onChange={handleFieldsChange}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                style={{ marginRight: 8 }}
                checked={selected}
                size="small"
              />
              {option.field_template_name}
            </li>
          )}
          renderGroup={(params) => (
            <li key={params.key}>
              <GroupHeader>{params.group}</GroupHeader>
              <GroupItems>{params.children}</GroupItems>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Select Fields"
              placeholder="Select Fields"
              color="primary"
            />
          )}
          sx={textFieldStyle}
        />
      </FormControl>
    </div>
  )
}

export default EmailFieldsSettingUI
